import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";

const SOURCE_KEY = "cfg_source_v1";

/**
 * Classify the current visit's traffic source — runs once per session.
 *
 * Priority:
 *   1. utm_source (+ utm_medium for paid attribution)   — most reliable
 *   2. document.referrer hostname → friendly bucket
 *   3. No referrer → "direct"
 *
 * Internal navigation referrers (cfograde.com, preview hosts, localhost)
 * are intentionally ignored so a click from /report → /trends doesn't
 * overwrite the original "Reddit" attribution recorded on the landing hit.
 */
function detectSource() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = (params.get("utm_source") || "").toLowerCase().trim();
    const utmMedium = (params.get("utm_medium") || "").toLowerCase().trim();

    // Explicit UTM tags always win — these come straight from links we control.
    if (utmSource) {
      if (utmMedium === "cpc" || utmMedium === "paid") {
        if (utmSource === "google") return "google_ads";
        if (utmSource === "bing") return "bing_ads";
        if (utmSource === "facebook" || utmSource === "meta") return "facebook_ads";
        if (utmSource === "reddit") return "reddit_ads";
      }
      // Anything else: trust the operator's tag verbatim
      // (newsletter, partner, podcast, etc.)
      return utmSource.replace(/[^a-z0-9_-]/g, "").slice(0, 32) || "direct";
    }

    const ref = document.referrer || "";
    if (!ref) return "direct";

    const host = new URL(ref).hostname.replace(/^www\./, "").toLowerCase();

    // Internal navigation — return null so we don't overwrite a previously
    // captured external source.
    if (
      host.endsWith("cfograde.com") ||
      host.includes("preview.emergentagent.com") ||
      host === "localhost" ||
      host === "127.0.0.1"
    ) {
      return null;
    }

    if (host.endsWith("reddit.com") || host === "old.reddit.com") return "reddit";
    if (host === "google.com" || host.endsWith(".google.com") || /^google\.[a-z.]+$/.test(host)) return "google";
    if (host.endsWith("bing.com")) return "bing";
    if (host.endsWith("duckduckgo.com")) return "duckduckgo";
    if (host.endsWith("twitter.com") || host === "x.com" || host.endsWith(".x.com") || host === "t.co") return "twitter";
    if (host.endsWith("linkedin.com") || host === "lnkd.in") return "linkedin";
    if (host.endsWith("facebook.com") || host === "m.facebook.com" || host === "fb.com") return "facebook";
    if (host.endsWith("instagram.com")) return "instagram";
    if (host.endsWith("youtube.com") || host === "youtu.be") return "youtube";
    if (host.endsWith("tiktok.com")) return "tiktok";
    if (host.endsWith("hackernews.com") || host === "news.ycombinator.com") return "hackernews";
    if (host.endsWith("producthunt.com")) return "producthunt";

    return `other:${host.slice(0, 48)}`;
  } catch {
    return "direct";
  }
}

/**
 * Returns the sticky session source. Computed once on first call, then
 * persisted in sessionStorage so every subsequent pageview during the
 * session is attributed to the same origin.
 */
function getOrCaptureSource() {
  if (typeof window === "undefined") return "direct";
  try {
    const cached = sessionStorage.getItem(SOURCE_KEY);
    if (cached) return cached;
  } catch {
    /* sessionStorage blocked — just compute fresh */
  }
  const fresh = detectSource() || "direct";
  try {
    sessionStorage.setItem(SOURCE_KEY, fresh);
  } catch {
    /* ignore quota / privacy mode errors */
  }
  return fresh;
}

/**
 * Fires a single pageview to the backend whenever the route changes.
 *
 * Design notes:
 * - Best-effort, non-blocking — never await, never surface errors to the UI.
 * - Deduped within a session: navigating /a → /a (e.g., React StrictMode double
 *   mounts, or replace-state spam) only sends one hit.
 * - Skips localhost / dev domains by default so we don't pollute prod counts
 *   when developing.
 * - Skips /admin so the operator's own dashboard visits don't inflate stats.
 */
export function usePageviewTracker() {
  const location = useLocation();
  const lastSent = useRef(null);

  useEffect(() => {
    const path = location.pathname || "/";

    // Don't double-fire when the same path re-mounts.
    if (lastSent.current === path) return;
    lastSent.current = path;

    // Don't pollute counts from the operator dashboard itself.
    if (path === "/admin" || path.startsWith("/admin/")) return;

    // Don't count localhost during dev. (Production hits cfograde.com /
    // *.preview.emergentagent.com only.)
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1") return;
    }

    // Fire-and-forget. Wrapped in setTimeout(0) so React commit isn't blocked.
    setTimeout(() => {
      let tz = null;
      try {
        // IANA tz like 'America/New_York' — backend maps to country code.
        // Browser support: 95%+, returns undefined on very old browsers.
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      } catch {
        tz = null;
      }
      const source = getOrCaptureSource();
      api
        .post("/track/pageview", { path, tz, source })
        .catch(() => {
          /* analytics must never break the app */
        });
    }, 0);
  }, [location.pathname]);
}
